/**
 * The write routes of the hot translations (#4034), declared once with `defineRoute`: the backend registers each route
 * from its contract (#4039) and the editor calls it with `callContract`. The four reads are screens (#4038) over the
 * shapes of `translations.types.ts`.
 *
 * Every body is a `z.strictObject`: a key nobody declared is a 400, never a silently dropped field. A write names the
 * version it saw (`expectedVersion`) so the server can refuse it when the key moved in the meantime.
 */

import { z } from 'zod/v4'

import { defineRoute } from '../../contract/index.js'
import { changeReasonSchema } from '../content/change-reason.js'

import {
  translationKeySchema,
  translationLocaleSchema,
  translationNamespaceSchema,
  translationPublicationSchema,
  translationRowSchema,
  translationValueSchema,
  translationVersionSchema,
} from './translations.types.js'

/** Which key and which version of it a draft write is about. */
const translationKeyVersionBodySchema = z.strictObject({
  locale: translationLocaleSchema,
  namespace: translationNamespaceSchema,
  key: translationKeySchema,
  expectedVersion: translationVersionSchema,
})

export const saveTranslationDraftBodySchema = translationKeyVersionBodySchema.extend({
  value: translationValueSchema,
})
export type SaveTranslationDraftBody = z.infer<typeof saveTranslationDraftBodySchema>

export const discardTranslationDraftBodySchema = translationKeyVersionBodySchema
export const proposeTranslationRemovalBodySchema = translationKeyVersionBodySchema

export const restoreTranslationRevisionBodySchema = z.strictObject({
  revisionId: z.guid(),
  expectedVersion: translationVersionSchema,
})

export const publishTranslationsBodySchema = z.strictObject({
  /** The language to publish, or `null` for every language with pending drafts. */
  locale: translationLocaleSchema.nullable(),
  reason: changeReasonSchema,
  /** How many drafts the person saw: the server refuses the publication when that number changed. */
  expectedDraftCount: z.number().int().min(1),
  acknowledgedWarnings: z.boolean(),
})
export type PublishTranslationsBody = z.infer<typeof publishTranslationsBodySchema>

/** An empty strict body: the route takes only its path parameter, and an invented key is a 400. */
export const revertTranslationPublicationBodySchema = z.strictObject({})

/** Why a key of the reverted publication did not get a draft: it has one already, or it changed since. */
export const TRANSLATION_REVERT_SKIP_REASONS = ['has-draft', 'changed-since'] as const

export const publishTranslationsResponseSchema = z.strictObject({
  publication: translationPublicationSchema,
  affected: z.array(
    z.strictObject({
      locale: translationLocaleSchema,
      namespaces: z.array(translationNamespaceSchema).min(1),
    }),
  ),
})
export type PublishTranslationsResponse = z.infer<typeof publishTranslationsResponseSchema>

export const revertTranslationPublicationResponseSchema = z.strictObject({
  createdDrafts: z.number().int().min(0),
  skipped: z.array(
    z.strictObject({
      locale: translationLocaleSchema,
      namespace: translationNamespaceSchema,
      key: translationKeySchema,
      reason: z.enum(TRANSLATION_REVERT_SKIP_REASONS),
    }),
  ),
})
export type RevertTranslationPublicationResponse = z.infer<
  typeof revertTranslationPublicationResponseSchema
>

export const translationRoutes = {
  saveDraft: defineRoute({
    id: 'translations.draft.save',
    method: 'PUT',
    path: '/api/admin/system/translations/draft',
    access: 'super_admin',
    body: saveTranslationDraftBodySchema,
    response: translationRowSchema,
    summary: 'Save the draft of one translation key',
    tags: ['admin', 'translations'],
  }),
  discardDraft: defineRoute({
    id: 'translations.draft.discard',
    method: 'DELETE',
    path: '/api/admin/system/translations/draft',
    access: 'super_admin',
    body: discardTranslationDraftBodySchema,
    response: translationRowSchema,
    summary: 'Discard the draft of one translation key',
    tags: ['admin', 'translations'],
  }),
  proposeRemoval: defineRoute({
    id: 'translations.draft.proposeRemoval',
    method: 'POST',
    path: '/api/admin/system/translations/draft/removal',
    access: 'super_admin',
    body: proposeTranslationRemovalBodySchema,
    response: translationRowSchema,
    summary: 'Propose returning a published override to the catalogue',
    tags: ['admin', 'translations'],
  }),
  restoreRevision: defineRoute({
    id: 'translations.draft.restore',
    method: 'POST',
    path: '/api/admin/system/translations/draft/restore',
    access: 'super_admin',
    body: restoreTranslationRevisionBodySchema,
    response: translationRowSchema,
    summary: 'Create a draft from a revision of the history',
    tags: ['admin', 'translations'],
  }),
  publish: defineRoute({
    id: 'translations.publications.publish',
    method: 'POST',
    path: '/api/admin/system/translations/publications',
    access: 'super_admin',
    body: publishTranslationsBodySchema,
    response: publishTranslationsResponseSchema,
    summary: 'Publish the pending translation drafts with a reason',
    tags: ['admin', 'translations'],
  }),
  revertDrafts: defineRoute({
    id: 'translations.publications.revertDrafts',
    method: 'POST',
    path: '/api/admin/system/translations/publications/:id/revert-drafts',
    access: 'super_admin',
    params: z.object({ id: z.guid() }),
    body: revertTranslationPublicationBodySchema,
    response: revertTranslationPublicationResponseSchema,
    summary: 'Create the drafts that undo a publication (does not publish)',
    tags: ['admin', 'translations'],
  }),
} as const
